'use client';

import { useShopifyCookies } from '@shopify/hydrogen-react';
import { useLogger } from 'next-axiom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useUserInteraction } from '../../lib/hooks/use-user-interaction';
import { useShopifyAnalytics } from '../hooks/use-shopify-analytics';
import {
  areCookiesEnabled,
  hasShopifyCookies,
} from '../utils/shopify-cookie-utils';

interface Props {
  hasConsent: boolean;
}

const MAX_COOKIE_CHECK_RETRIES = 10; // Reduced from 20
const COOKIE_CHECK_INTERVAL_MS = 100; // Increased from 50ms

export function ShopifyAnalytics({ hasConsent }: Props) {
  const hasUserInteracted = useUserInteraction();
  const { sendPageView, pathname } = useShopifyAnalytics();

  const [isInitialized, setIsInitialized] = useState(false);
  const [cookiesReady, setCookiesReady] = useState(false);

  // Use refs to track state and prevent race conditions
  const retryCountRef = useRef(0);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const lastPathnameRef = useRef<string>('');

  const logger = useLogger().with({
    location: 'shopify-analytics.tsx',
  });

  // Initialize Shopify cookies only after user interaction
  useShopifyCookies({
    hasUserConsent: hasUserInteracted && hasConsent,
  });

  // Memoized cookie check function
  // biome-ignore lint/correctness/useExhaustiveDependencies: Logger is not a dependency
  const checkCookies = useCallback(() => {
    if (!isMountedRef.current) return;

    try {
      if (hasShopifyCookies()) {
        setCookiesReady(true);
        retryCountRef.current = 0;
      } else if (retryCountRef.current < MAX_COOKIE_CHECK_RETRIES) {
        retryCountRef.current += 1;
        timeoutIdRef.current = setTimeout(
          checkCookies,
          COOKIE_CHECK_INTERVAL_MS
        );
      } else {
        // Give up after max retries
        setCookiesReady(true);
      }
    } catch (error) {
      logger.error('Cookie check error', { error });
      setCookiesReady(true);
    }
  }, []);

  // Initialize analytics after user interaction
  useEffect(() => {
    if (!hasUserInteracted || isInitialized) return;

    setIsInitialized(true);

    // Check if cookies are enabled
    if (!areCookiesEnabled()) {
      setCookiesReady(true);
      return;
    }

    // Start cookie checking
    checkCookies();
  }, [hasUserInteracted, isInitialized, checkCookies]);

  // Send page view analytics with debouncing
  // biome-ignore lint/correctness/useExhaustiveDependencies: Logger is not a dependency
  useEffect(() => {
    if (!hasConsent || !isInitialized || !cookiesReady) return;
    if (lastPathnameRef.current === pathname) return; // Prevent duplicate calls

    lastPathnameRef.current = pathname;

    // Small delay to batch rapid navigation changes
    const timeoutId = setTimeout(() => {
      if (isMountedRef.current) {
        sendPageView('PAGE_VIEW').catch((error) => {
          logger.error('Failed to send page view', { error });
        });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [pathname, hasConsent, isInitialized, cookiesReady, sendPageView]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
  }, []);

  return null;
}
